import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface ResonanceStrataProps {
  artistId: string;
  accentColor: string;
  compact?: boolean;
  refreshTrigger?: number;
}

interface ResonanceRecord {
  id: string;
  amount: number;
  created_at: string;
  user_id: string;
  display_name: string | null;
}

export function ResonanceStrata({ artistId, accentColor, compact = false, refreshTrigger = 0 }: ResonanceStrataProps) {
  const [records, setRecords] = useState<ResonanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const prevRecordIds = useRef<Set<string>>(new Set());
  const isFirstLoad = useRef(true);

  const fetchResonanceData = async () => {
    try {
      const { data, error } = await supabase
        .from('artist_revenues')
        .select(`
          id,
          amount,
          created_at,
          user_id,
          profiles!artist_revenues_user_id_fkey (
            display_name
          )
        `)
        .eq('artist_id', artistId)
        .order('amount', { ascending: false });

      if (error) throw error;

      const formatted = (data || []).map((record: any) => ({
        id: record.id,
        amount: record.amount,
        created_at: record.created_at,
        user_id: record.user_id,
        display_name: record.profiles?.display_name || 'Anonymous',
      }));

      if (!isFirstLoad.current) {
        // 新しく追加されたIDを検出
        const incoming = new Set(formatted.map((r) => r.id));
        const added = new Set<string>();
        incoming.forEach((id) => {
          if (!prevRecordIds.current.has(id)) added.add(id);
        });
        if (added.size > 0) {
          setNewIds(added);
          // 2秒後にアニメーションクラスを外す
          setTimeout(() => setNewIds(new Set()), 2000);
        }
      }

      prevRecordIds.current = new Set(formatted.map((r) => r.id));
      isFirstLoad.current = false;
      setRecords(formatted);
    } catch (err) {
      console.error('Failed to fetch resonance data:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  // 初回ロード
  useEffect(() => {
    if (artistId && !artistId.startsWith(':')) {
      setLoading(true);
      isFirstLoad.current = true;
      fetchResonanceData();
    } else {
      setLoading(false);
    }
  }, [artistId]);

  // refreshTrigger が変化したら再取得（初回は除く）
  useEffect(() => {
    if (refreshTrigger > 0 && artistId && !artistId.startsWith(':')) {
      fetchResonanceData();
    }
  }, [refreshTrigger]);

  const getStrataLevel = (amount: number): 'deep' | 'middle' | 'surface' => {
    if (amount >= 5000) return 'deep';
    if (amount >= 1000) return 'middle';
    return 'surface';
  };

  const getStrataStyle = (level: 'deep' | 'middle' | 'surface') => {
    switch (level) {
      case 'deep':
        return {
          borderColor: '#ff0040',
          boxShadow: '0 0 30px #ff004040, inset 0 0 20px #ff004020',
          background: 'linear-gradient(135deg, #ff004015, #ff004008)',
          glowColor: '#ff0040',
        };
      case 'middle':
        return {
          borderColor: `${accentColor}60`,
          boxShadow: `0 0 20px ${accentColor}30, inset 0 0 15px ${accentColor}15`,
          background: `linear-gradient(135deg, ${accentColor}10, ${accentColor}05)`,
          glowColor: accentColor,
        };
      case 'surface':
        return {
          borderColor: 'rgba(255,255,255,0.1)',
          boxShadow: '0 0 10px rgba(255,255,255,0.05)',
          background: 'rgba(255,255,255,0.02)',
          glowColor: 'rgba(255,255,255,0.3)',
        };
    }
  };

  const getStrataLabel = (level: 'deep' | 'middle' | 'surface'): string => {
    switch (level) {
      case 'deep': return 'DEEP CORE';
      case 'middle': return 'MIDDLE STRATA';
      case 'surface': return 'SURFACE';
    }
  };

  if (loading) {
    return (
      <div className={`${compact ? 'py-8' : 'py-12'} text-center`}>
        <i className="ri-loader-4-line text-2xl text-gray-700 animate-spin" />
      </div>
    );
  }

  if (records.length === 0) {
    return null;
  }

  return (
    <div className={compact ? 'space-y-6' : 'space-y-8'}>
      {/* アニメーション用スタイル */}
      <style>{`
        @keyframes strataSlideIn {
          0% {
            opacity: 0;
            transform: translateY(-16px) scale(0.97);
          }
          60% {
            opacity: 1;
            transform: translateY(2px) scale(1.01);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes strataPulseGlow {
          0%, 100% { box-shadow: var(--base-shadow); }
          50% { box-shadow: var(--base-shadow), 0 0 60px var(--glow-color); }
        }
        .strata-new-entry {
          animation: strataSlideIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards,
                     strataPulseGlow 1.2s ease-in-out 0.6s 2;
        }
      `}</style>

      {/* タイトル */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-3 px-6 py-3 rounded-full border-2"
          style={{
            borderColor: `${accentColor}40`,
            background: `linear-gradient(135deg, ${accentColor}15, transparent)`,
            boxShadow: `0 0 30px ${accentColor}20`,
          }}
        >
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
          <h3
            className="text-xl font-black tracking-[0.3em] uppercase"
            style={{ color: accentColor }}
          >
            RESONANCE STRATA
          </h3>
          <div
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: accentColor, boxShadow: `0 0 10px ${accentColor}` }}
          />
        </div>
        <p className="text-gray-600 text-xs tracking-[0.2em] mt-3 uppercase">共鳴の地層</p>
      </div>

      {/* 地層リスト */}
      <div className={`space-y-${compact ? '3' : '4'} max-w-3xl mx-auto`}>
        {records.map((record) => {
          const level = getStrataLevel(record.amount);
          const style = getStrataStyle(level);
          const label = getStrataLabel(level);
          const isNew = newIds.has(record.id);

          return (
            <div
              key={record.id}
              className={`relative border-2 rounded-xl ${compact ? 'p-4' : 'p-5'} transition-all duration-300 hover:scale-[1.02] ${isNew ? 'strata-new-entry' : ''}`}
              style={{
                borderColor: isNew ? (level === 'deep' ? '#ff0040' : accentColor) : style.borderColor,
                boxShadow: isNew
                  ? `${style.boxShadow}, 0 0 50px ${style.glowColor}60`
                  : style.boxShadow,
                background: style.background,
                '--base-shadow': style.boxShadow,
                '--glow-color': style.glowColor,
              } as React.CSSProperties}
            >
              {/* 新着バッジ */}
              {isNew && (
                <div
                  className="absolute -top-3 right-6 px-3 py-1 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border-2 animate-pulse"
                  style={{
                    background: '#000',
                    borderColor: style.glowColor,
                    color: style.glowColor,
                    boxShadow: `0 0 20px ${style.glowColor}60`,
                  }}
                >
                  NEW
                </div>
              )}

              {/* 地層レベルバッジ */}
              <div
                className="absolute -top-3 left-6 px-3 py-1 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border-2"
                style={{
                  background: '#000',
                  borderColor: style.borderColor,
                  color: style.glowColor,
                  boxShadow: `0 0 15px ${style.glowColor}40`,
                }}
              >
                {label}
              </div>

              <div className="flex items-center gap-4">
                {/* アバター */}
                <div
                  className="relative flex-shrink-0 rounded-full flex items-center justify-center border-2"
                  style={{
                    width: compact ? '48px' : '56px',
                    height: compact ? '48px' : '56px',
                    background: 'rgba(255,255,255,0.05)',
                    borderColor: style.borderColor,
                  }}
                >
                  <i className="ri-user-line text-xl text-gray-600" />
                </div>

                {/* ユーザー情報 */}
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-black tracking-wider uppercase truncate ${compact ? 'text-sm' : 'text-base'}`}
                    style={{
                      color: style.glowColor,
                      fontWeight: level === 'deep' ? 900 : level === 'middle' ? 700 : 600,
                      textShadow: level === 'deep' ? `0 0 10px ${style.glowColor}60` : 'none',
                    }}
                  >
                    {record.display_name}
                  </p>
                  <p className="text-gray-600 text-xs tracking-wider mt-0.5">
                    {new Date(record.created_at).toLocaleDateString('ja-JP', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </p>
                </div>

                {/* 金額 */}
                <div className="text-right flex-shrink-0">
                  <div
                    className={`font-black tracking-wider ${compact ? 'text-xl' : 'text-2xl'}`}
                    style={{
                      color: style.glowColor,
                      fontWeight: level === 'deep' ? 900 : level === 'middle' ? 700 : 600,
                      textShadow: level === 'deep' ? `0 0 15px ${style.glowColor}80` : 'none',
                    }}
                  >
                    ¥{record.amount.toLocaleString()}
                  </div>
                  <p className="text-gray-700 text-[10px] tracking-wider mt-1 uppercase">
                    RESONANCE
                  </p>
                </div>
              </div>

              {/* Deep Core専用エフェクト */}
              {level === 'deep' && (
                <>
                  <div
                    className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at 50% 50%, ${style.glowColor}40, transparent 70%)`,
                    }}
                  />
                  <div
                    className="absolute -inset-[1px] rounded-xl opacity-30 pointer-events-none animate-pulse"
                    style={{
                      background: `linear-gradient(45deg, ${style.glowColor}20, transparent, ${style.glowColor}20)`,
                    }}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* 総計表示 */}
      <div
        className={`max-w-3xl mx-auto ${compact ? 'p-4' : 'p-6'} rounded-xl border-2 text-center`}
        style={{
          borderColor: `${accentColor}30`,
          background: `linear-gradient(135deg, ${accentColor}08, transparent)`,
          boxShadow: `0 0 20px ${accentColor}15`,
        }}
      >
        <p className="text-gray-500 text-xs tracking-[0.2em] uppercase mb-2">Total Resonance</p>
        <div
          className="text-3xl font-black tracking-wider"
          style={{ color: accentColor, textShadow: `0 0 20px ${accentColor}60` }}
        >
          ¥{records.reduce((sum, r) => sum + r.amount, 0).toLocaleString()}
        </div>
        <p className="text-gray-700 text-[10px] tracking-wider mt-2 uppercase">
          {records.length} Supporters
        </p>
      </div>
    </div>
  );
}
